import { useQuery } from "@apollo/client/react";
import { useCallback, useEffect } from "react";

import FlashContext from "components/FlashMessages/FlashContext";
import useDebounceAsync from "hooks/useDebounceAsync";
import useTypedContext from "hooks/useTypedContext";

import { StackFormContext } from "../context";
import { validateRequired } from "../utils";
import { SEARCH_STACK_NAME_SUGGESTIONS } from "./gql";

const useNameAsyncValidation = () => {
  const { setAsyncValidationLoading, createdStackId } = useTypedContext(StackFormContext);
  const { onError } = useTypedContext(FlashContext);

  const stackIsAlreadyCreated = typeof createdStackId !== "undefined" && createdStackId.length > 0;

  const { refetch: loadSearchStacksSuggestions } = useQuery(SEARCH_STACK_NAME_SUGGESTIONS, {
    variables: { input: { fields: null, fullTextSearch: null, predicates: null } },
    skip: true,
    fetchPolicy: "no-cache",
  });

  const asyncValidation = useCallback(
    async (name: string): Promise<true | string> => {
      try {
        setAsyncValidationLoading(true);
        const response = await loadSearchStacksSuggestions({
          input: {
            fullTextSearch: name,
            predicates: [],
            fields: ["name"],
          },
        });

        const possibleValues =
          response?.data?.searchStacksSuggestions.fields[0].possibleValuesString;

        if (
          possibleValues?.counts?.length &&
          possibleValues.values.some((item) => item.toLowerCase() === name.trim().toLowerCase())
        ) {
          return "Stack with this name already exists";
        }

        return true;
      } catch (error) {
        onError(error);

        return "Something went wrong";
      } finally {
        setAsyncValidationLoading(false);
      }
    },

    [setAsyncValidationLoading, loadSearchStacksSuggestions, onError]
  );

  const debouncedAsyncValidation = useDebounceAsync(asyncValidation);

  const validateName = useCallback(
    async (value: string) => {
      const requiredValidation = validateRequired("Name")(value);

      if (requiredValidation === true && !stackIsAlreadyCreated) {
        return await debouncedAsyncValidation(value);
      }

      return requiredValidation;
    },
    [debouncedAsyncValidation, stackIsAlreadyCreated]
  );

  useEffect(() => () => setAsyncValidationLoading(false), [setAsyncValidationLoading]);

  return validateName;
};

export default useNameAsyncValidation;
